<template>
  <transition name="cs-fade">
    <div v-if="visible" class="cs-root" :class="{ 'cs-root--mobile': !isPc, 'cs-root--pc': isPc }" @click="emojiOpen = false">
      <div class="cs-panel">
        <header class="cs-header">
          <div class="cs-header__left">
            <div class="cs-avatar">
              <span>CS</span>
              <i class="cs-online" />
            </div>
            <div class="cs-header__meta">
              <div class="cs-title">{{ $t('customerService.title') }}</div>
              <div class="cs-subtitle">{{ $t('customerService.subtitle') }}</div>
            </div>
          </div>
          <button type="button" class="cs-close" @click="close">×</button>
        </header>
        <div ref="messageList" class="cs-messages">
          <div v-if="loadingMore" class="cs-loading">{{ $t('customerService.loading') }}</div>
          <div v-if="!loadingMore && messages.length === 0" class="cs-empty">
            {{ $t('customerService.empty') }}
          </div>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="cs-msg-row"
            :class="msg.senderType === 1 ? 'cs-msg-row--mine' : 'cs-msg-row--other'"
          >
            <div class="cs-bubble">
              <img
                v-if="msg.msgType === 'image'"
                :src="msg.content"
                class="cs-image"
                @click="previewImage(msg.content)"
              />
              <span v-else class="cs-text">{{ msg.content }}</span>
            </div>
            <div class="cs-time">{{ formatTime(msg.createTime) }}</div>
          </div>
        </div>
        <footer class="cs-footer" @click.stop>
          <div class="cs-tools">
            <button type="button" class="cs-icon-btn" :title="$t('customerService.emoji')" @click.stop="emojiOpen = !emojiOpen">
              😊
            </button>
            <label class="cs-icon-btn" :title="$t('customerService.sendImage')">
              <input type="file" accept="image/*" class="cs-file" @change="onImagePick" />
              <span>＋</span>
            </label>
            <emoji-picker :visible="emojiOpen" @select="insertEmoji" />
          </div>
          <textarea
            v-model="draft"
            class="cs-input"
            rows="2"
            :placeholder="$t('customerService.placeholder')"
            @keydown.enter.exact.prevent="sendText"
          />
          <button type="button" class="cs-send" :disabled="sending || !(draft || '').trim()" @click="sendText">
            {{ $t('customerService.send') }}
          </button>
        </footer>
      </div>
      <div v-if="!isPc" class="cs-backdrop" @click="close" />
      <div v-if="previewUrl" class="cs-preview" @click="previewUrl = ''">
        <img :src="previewUrl" alt="" />
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from 'vuex'
import { csBus } from '@/utils/csBus'
import { getToken } from '@/utils/auth'
import { VUE_APP_WS_URL } from '@/config'
import { getCsSession, getCsMessages, sendCsMessage, markCsRead } from '@/api/cs'
import request from '@/utils/request'
import Compressor from 'compressorjs'
import EmojiPicker from './EmojiPicker.vue'

export default {
  name: 'CustomerChat',
  components: { EmojiPicker },
  data() {
    return {
      visible: false,
      session: null,
      messages: [],
      draft: '',
      sending: false,
      loadingMore: false,
      ws: null,
      pingTimer: null,
      reconnectTimer: null,
      emojiOpen: false,
      previewUrl: ''
    }
  },
  computed: {
    ...mapState(['isPc'])
  },
  mounted() {
    csBus.$on('open', this.open)
  },
  beforeDestroy() {
    csBus.$off('open', this.open)
    this.teardownWs()
  },
  methods: {
    async open() {
      this.visible = true
      this.emojiOpen = false
      try {
        const res = await getCsSession()
        const code = res && res.code != null ? Number(res.code) : NaN
        if (code !== 200 && code !== 0) {
          throw new Error((res && (res.message || res.msg)) || 'session error')
        }
        this.session = res.result
        await this.loadMessages()
        this.connectWs()
        this.$nextTick(() => this.scrollBottom())
        if (this.session && this.session.id) {
          markCsRead({ sessionId: this.session.id })
        }
      } catch (e) {
        console.error(e)
        this.$message.error(this.$t('customerService.error'))
        this.visible = false
      }
    },
    close() {
      this.visible = false
      this.emojiOpen = false
      this.previewUrl = ''
      this.teardownWs()
    },
    async loadMessages(beforeId) {
      if (!this.session) return
      this.loadingMore = !!beforeId
      try {
        const res = await getCsMessages({
          sessionId: this.session.id,
          beforeId,
          limit: 50
        })
        const list = (res.result || [])
        if (beforeId) {
          this.messages = [...list, ...this.messages]
        } else {
          this.messages = list
        }
      } finally {
        this.loadingMore = false
      }
    },
    connectWs() {
      this.teardownWs()
      const token = getToken()
      if (!token) return
      const base = (VUE_APP_WS_URL || '').replace(/\/$/, '')
      const url = `${base}/cs/user?token=${encodeURIComponent(token)}`
      this.ws = new WebSocket(url)
      this.ws.onmessage = (ev) => this.onWsMessage(ev.data)
      this.ws.onclose = () => this.scheduleReconnect()
      this.pingTimer = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }))
        }
      }, 25000)
    },
    scheduleReconnect() {
      if (!this.visible) return
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = setTimeout(() => this.connectWs(), 3000)
    },
    teardownWs() {
      clearInterval(this.pingTimer)
      clearTimeout(this.reconnectTimer)
      if (this.ws) {
        this.ws.close()
        this.ws = null
      }
    },
    onWsMessage(raw) {
      try {
        const envelope = JSON.parse(raw)
        if (envelope.event === 'message' && envelope.data) {
          const msg = envelope.data
          if (this.session && msg.sessionId === this.session.id) {
            if (!this.messages.find((m) => m.id === msg.id)) {
              this.messages.push(msg)
              this.$nextTick(() => this.scrollBottom())
            }
            if (msg.senderType === 2) {
              markCsRead({ sessionId: this.session.id })
            }
          }
        }
        if (envelope.event === 'message_delete' && envelope.data) {
          const id = Number(envelope.data.messageId)
          this.messages = this.messages.filter((m) => Number(m.id) !== id)
        }
      } catch (e) {
        console.warn('cs ws parse', e)
      }
    },
    insertEmoji(emoji) {
      this.draft = (this.draft || '') + emoji
    },
    async sendText() {
      const text = (this.draft || '').trim()
      if (!text || !this.session) return
      this.draft = ''
      this.emojiOpen = false
      await this.sendPayload('text', text)
    },
    async sendPayload(msgType, content) {
      if (!this.session || this.sending) return
      this.sending = true
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(
            JSON.stringify({
              action: 'send',
              sessionId: this.session.id,
              msgType,
              content
            })
          )
        } else {
          const res = await sendCsMessage({
            sessionId: this.session.id,
            msgType,
            content
          })
          if (res.result) {
            this.appendLocal(res.result)
          }
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      } finally {
        this.sending = false
      }
    },
    appendLocal(msg) {
      if (!this.messages.find((m) => m.id === msg.id)) {
        this.messages.push(msg)
        this.$nextTick(() => this.scrollBottom())
      }
    },
    onImagePick(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 1280,
        success: (blob) => this.uploadImage(blob),
        error: () => this.uploadImage(file)
      })
    },
    async uploadImage(fileOrBlob) {
      const form = new FormData()
      form.append('file', fileOrBlob, 'cs.jpg')
      try {
        const res = await request({
          url: '/swx/customer/user/upload',
          method: 'post',
          data: form,
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        const url = res.url || (res.result && res.result.url)
        if (url) {
          await this.sendPayload('image', url)
        }
      } catch (e) {
        this.$message.error(this.$t('customerService.error'))
      }
    },
    previewImage(url) {
      this.previewUrl = url
    },
    scrollBottom() {
      const el = this.$refs.messageList
      if (el) el.scrollTop = el.scrollHeight
    },
    formatTime(t) {
      if (!t) return ''
      if (typeof t === 'number') {
        const d = new Date(t)
        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`
      }
      const d = new Date(t)
      if (!Number.isNaN(d.getTime())) {
        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`
      }
      const s = String(t)
      return s.length >= 16 ? s.slice(11, 16) : s
    }
  }
}
</script>

<style lang="scss" scoped>
.cs-root {
  position: fixed;
  z-index: 10000;
  font-family: Inter, "Segoe UI", system-ui, -apple-system, sans-serif;
}
.cs-root--pc {
  right: 24px;
  bottom: 24px;
  width: 400px;
  height: 580px;
}
.cs-root--mobile {
  inset: 0;
  display: flex;
  align-items: stretch;
  justify-content: center;
}
.cs-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  z-index: -1;
}
.cs-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #111827;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(17, 24, 39, 0.22);
}
.cs-root--pc .cs-panel {
  width: 100%;
  height: 100%;
  border-radius: 20px;
}
.cs-root--mobile .cs-panel {
  width: 100%;
  height: calc(100% - 12px);
  margin-top: 12px;
  border-radius: 20px 20px 0 0;
  position: relative;
  z-index: 1;
  overflow: hidden;
}
.cs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: linear-gradient(135deg, #111827 0%, #1f2937 100%);
  color: #fff;
  flex-shrink: 0;
}
.cs-header__left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.cs-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.cs-online {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid #111827;
}
.cs-header__meta {
  min-width: 0;
}
.cs-title {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.01em;
}
.cs-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.62);
}
.cs-close {
  border: none;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: rgba(255, 255, 255, 0.16);
  }
}
.cs-messages {
  flex: 1;
  overflow-y: auto;
  padding: 18px 16px;
  background:
    radial-gradient(circle at top right, rgba(99, 102, 241, 0.06), transparent 30%),
    #f6f7f9;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.cs-loading,
.cs-empty {
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  margin: 12px 0 8px;
}
.cs-msg-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  max-width: 78%;
  width: fit-content;
}
.cs-msg-row--mine {
  align-self: flex-end;
  align-items: flex-end;
  margin-left: auto;
}
.cs-msg-row--other {
  align-self: flex-start;
  align-items: flex-start;
  margin-right: auto;
}
.cs-bubble {
  padding: 10px 13px;
  word-break: break-word;
}
.cs-msg-row--mine .cs-bubble {
  background: #111827;
  color: #fff;
  border-radius: 16px 16px 4px 16px;
  box-shadow: 0 8px 18px rgba(17, 24, 39, 0.14);
}
.cs-msg-row--other .cs-bubble {
  background: #fff;
  color: #111827;
  border-radius: 16px 16px 16px 4px;
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.06);
}
.cs-text {
  font-size: 14px;
  line-height: 1.55;
  white-space: pre-wrap;
}
.cs-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 10px;
  cursor: pointer;
  display: block;
}
.cs-time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 6px;
  line-height: 1.2;
}
.cs-footer {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef0f3;
  background: #fff;
  flex-shrink: 0;
}
.cs-tools {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding-bottom: 2px;
}
.cs-icon-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  color: #111827;
  margin: 0;
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
}
.cs-file {
  display: none;
}
.cs-input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 12px;
  min-height: 40px;
  max-height: 88px;
  resize: none;
  font-size: 14px;
  line-height: 1.4;
  outline: none;
  font-family: inherit;
  box-sizing: border-box;
  background: #f9fafb;
  &:focus {
    border-color: #111827;
    background: #fff;
  }
}
.cs-send {
  height: 40px;
  padding: 0 16px;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}
.cs-preview {
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(17, 24, 39, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 12px;
  }
}
.cs-fade-enter-active,
.cs-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.cs-fade-enter,
.cs-fade-leave-to {
  opacity: 0;
}
.cs-root--pc.cs-fade-enter,
.cs-root--pc.cs-fade-leave-to {
  transform: translateY(8px);
}
</style>
